import React, { useEffect, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ArrowUpDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { PartnerCardV2 } from "@/modules/hub/features/PartnerNetworkV2/components/PartnerCardV2/PartnerCardV2";
import { FeaturedCarouselV2 } from "@/modules/hub/features/ResourceLibraryV2/components/FeaturedCarouselV2/FeaturedCarouselV2";
import { usePartnerNetwork } from "../../hooks/usePartnerNetwork";
import { PartnerNetworkV2Skeleton } from "./PartnerNetworkV2Skeleton";
import { trackPartnerNetworkPageViewed } from "../../analytics/partnerNetworkAnalytics";

type SortOption = "newest" | "oldest" | "mostViewed" | "alphabetical";

export function PartnerNetworkV2() {
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackPartnerNetworkPageViewed();
      hasTrackedPageView.current = true;
    }
  }, []);

  const {
    form,
    resources,
    hasMore,
    totalCount,
    isInitialLoading,
    fetchMore,
    sortOption,
    setSortOption,
    handleFavoriteToggle,
  } = usePartnerNetwork();

  const showOnlyFavorites = Boolean(form.watch("isFavoriteOnly"));

  const toggleFavoritesFilter = () => {
    form.setValue("isFavoriteOnly", !showOnlyFavorites);
  };

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "mostViewed", label: "Most Viewed" },
    { value: "alphabetical", label: "Title A-Z" },
  ];

  if (isInitialLoading) {
    return <PartnerNetworkV2Skeleton />;
  }

  return (
    <MainPageWrapper
      subtitle="Browse resources and materials from our partner network"
      title="Partner Network"
    >
      <div className="space-y-6">
        {/* Featured Partners - only when no filters are active */}
        {!showOnlyFavorites && <FeaturedCarouselV2 context="partner-network" />}

        {/* Sort Controls and Favorites Toggle */}
        <div className="flex bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-1.5 flex-col items-start gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-0">
          <div className="flex items-center gap-4 w-full justify-between lg:w-auto lg:justify-start">
            <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
              {totalCount > 0 && `${totalCount} partners found`}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={showOnlyFavorites}
                id="partner-network-favorites-only"
                onCheckedChange={() => toggleFavoritesFilter()}
              />
              <label
                className="text-sm text-gray-600 dark:text-gray-400 cursor-pointer select-none whitespace-nowrap"
                htmlFor="partner-network-favorites-only"
              >
                Favorites only
              </label>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full lg:w-auto">
            <ArrowUpDown className="h-4 w-4 text-gray-500 shrink-0" />
            <Select
              onValueChange={(value) => setSortOption(value as SortOption)}
              value={sortOption}
            >
              <SelectTrigger className="w-full lg:w-[180px]">
                <SelectValue placeholder="Sort by..." />
              </SelectTrigger>
              <SelectContent>
                {sortOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Partner Grid with Infinite Scroll */}
        <InfiniteScroll
          dataLength={resources.length}
          endMessage={
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400">
                No more partners to load
              </p>
            </div>
          }
          hasMore={hasMore}
          loader={
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400">
                Loading more...
              </p>
            </div>
          }
          next={fetchMore}
          scrollableTarget="main-scroll-area"
          style={{ overflow: "visible" }}
        >
          {resources.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {showOnlyFavorites
                  ? "No favorite partners yet."
                  : "No partners found."}
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {resources.map((resource) => (
                <div className="fade-in" key={resource.id}>
                  <PartnerCardV2
                    onToggleFavorite={handleFavoriteToggle}
                    resource={resource}
                  />
                </div>
              ))}
            </div>
          )}
        </InfiniteScroll>
      </div>
    </MainPageWrapper>
  );
}